import React, { useEffect, useState } from 'react';
import { XIcon, CheckIcon, FileTextIcon, DownloadIcon, MailIcon, PrinterIcon } from 'lucide-react';
interface GenerateDocumentModalProps {
  settlement: {
    id: string;
    dealerName: string;
    amount: number;
    currency: string;
  };
  documentType: string | null;
  onClose: () => void;
  onGenerate: (documentType: string) => void;
}
export const GenerateDocumentModal: React.FC<GenerateDocumentModalProps> = ({
  settlement,
  documentType,
  onClose,
  onGenerate
}) => {
  const [selectedType, setSelectedType] = useState(documentType || 'invoice');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const documentTypes = [{
    id: 'invoice',
    name: 'Customer Invoice',
    description: 'Generate a customized invoice with specific line items',
    previewUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/sTVL3NFLjSiFiLynqjbUJ9/image.png'
  }, {
    id: 'tax_report',
    name: 'Tax Report',
    description: 'Generate a detailed tax report for accounting purposes',
    previewUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eQ3zS1QHFeDyfiCuiYuQyN/image.png'
  }, {
    id: 'commission_report',
    name: 'Commission Report',
    description: 'Generate a detailed breakdown of all commission calculations',
    previewUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/n97zhygTnEquGkke3MV2CD/image.png'
  }];
  // Set initial document type if provided
  useEffect(() => {
    if (documentType) {
      setSelectedType(documentType);
    }
  }, [documentType]);
  const handleGenerate = () => {
    setIsGenerating(true);
    // Simulate document generation
    setTimeout(() => {
      setIsGenerating(false);
      setIsGenerated(true);
      setPreviewUrl(documentTypes.find(doc => doc.id === selectedType)?.previewUrl || '');
    }, 2000);
  };
  const handleFinish = () => {
    onGenerate(selectedType);
  };
  // Handle click outside to close
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isGenerating) {
      onClose();
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={handleBackdropClick} data-id="element-527">
      <div className={`bg-white rounded-lg shadow-xl w-full ${isGenerated ? 'max-w-4xl' : 'max-w-2xl'} max-h-[90vh] overflow-hidden`} onClick={e => e.stopPropagation()} data-id="element-528">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-529">
          <h2 className="text-xl font-semibold text-gray-800" data-id="element-530">
            {isGenerated ? 'Document Generated' : 'Generate Document'}
          </h2>
          {!isGenerating && <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-531">
              <XIcon className="h-5 w-5" data-id="element-532" />
            </button>}
        </div>
        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-8rem)]" data-id="element-533">
          {isGenerated ? <div className="p-6" data-id="element-534">
              <div className="flex flex-col items-center justify-center mb-6" data-id="element-535">
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-3" data-id="element-536">
                  <CheckIcon className="h-6 w-6 text-green-600" data-id="element-537" />
                </div>
                <h3 className="text-lg font-medium text-gray-900" data-id="element-538">
                  {documentTypes.find(doc => doc.id === selectedType)?.name}{' '}
                  Generated Successfully
                </h3>
                <p className="text-gray-500 text-center mt-1" data-id="element-539">
                  The document has been generated and is ready for download
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden" data-id="element-540">
                <div className="px-4 py-3 bg-gray-100 border-b border-gray-200 flex justify-between items-center" data-id="element-541">
                  <div className="flex items-center" data-id="element-542">
                    <FileTextIcon className="h-5 w-5 text-blue-600 mr-2" data-id="element-543" />
                    <span className="font-medium text-gray-900" data-id="element-544">
                      {documentTypes.find(doc => doc.id === selectedType)?.name}{' '}
                      - {settlement.id}
                    </span>
                  </div>
                  <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full" data-id="element-545">
                    Generated
                  </span>
                </div>
                <div className="h-96 overflow-hidden" data-id="element-546">
                  <img src={previewUrl} alt="Document preview" className="w-full h-full object-contain" data-id="element-547" />
                </div>
                <div className="px-4 py-3 bg-gray-100 border-t border-gray-200 flex justify-between items-center" data-id="element-548">
                  <div className="text-xs text-gray-500" data-id="element-549">
                    <div data-id="element-550">
                      PDF • Generated on {new Date().toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-551">
                    <button className="p-2 text-gray-600 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors" data-id="element-552">
                      <PrinterIcon className="h-4 w-4" data-id="element-553" />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors" data-id="element-554">
                      <MailIcon className="h-4 w-4" data-id="element-555" />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors" data-id="element-556">
                      <DownloadIcon className="h-4 w-4" data-id="element-557" />
                    </button>
                  </div>
                </div>
              </div>
            </div> : isGenerating ? <div className="p-12 flex flex-col items-center justify-center" data-id="element-558">
              <div className="w-16 h-16 border-4 border-t-transparent border-blue-600 rounded-full animate-spin mb-6" data-id="element-559"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-560">
                Generating Document
              </h3>
              <p className="text-gray-500 text-center" data-id="element-561">
                Please wait while we generate your{' '}
                {documentTypes.find(doc => doc.id === selectedType)?.name.toLowerCase()}
                ...
              </p>
            </div> : <div className="p-6" data-id="element-562">
              <div className="mb-6" data-id="element-563">
                <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-564">
                  Settlement: {settlement.id}
                </h3>
                <div className="text-sm text-gray-500" data-id="element-565">
                  <p data-id="element-566">Dealer: {settlement.dealerName}</p>
                  <p data-id="element-567">
                    Amount: {settlement.currency}{' '}
                    {settlement.amount.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                  </p>
                </div>
              </div>
              <div className="mb-6" data-id="element-568">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-569">
                  Select Document Type
                </label>
                <div className="space-y-3" data-id="element-570">
                  {documentTypes.map(docType => <div key={docType.id} className={`border rounded-md p-4 cursor-pointer transition-colors ${selectedType === docType.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedType(docType.id)} data-id="element-571">
                      <div className="flex items-start" data-id="element-572">
                        <input type="radio" checked={selectedType === docType.id} onChange={() => setSelectedType(docType.id)} className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-573" />
                        <div className="ml-3" data-id="element-574">
                          <h4 className="text-sm font-medium text-gray-900" data-id="element-575">
                            {docType.name}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1" data-id="element-576">
                            {docType.description}
                          </p>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6" data-id="element-577">
                <p className="text-sm text-blue-700" data-id="element-578">
                  Generating a new document will replace any existing document
                  of the same type. The document will be available for download
                  immediately after generation.
                </p>
              </div>
            </div>}
        </div>
        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3" data-id="element-579">
          {!isGenerating && <>
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-580">
                {isGenerated ? 'Close' : 'Cancel'}
              </button>
              <button onClick={isGenerated ? handleFinish : handleGenerate} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors" data-id="element-581">
                {isGenerated ? 'Download Document' : 'Generate Document'}
              </button>
            </>}
        </div>
      </div>
    </div>;
};